// Product analytics (PostHog) wrapper.
//
// Inert by default. When REACT_APP_POSTHOG_KEY is unset (CRA only exposes
// REACT_APP_* vars to the client bundle), every export here is a no-op — same
// posture as the Sentry init in index.js, so local dev and tests never talk to
// PostHog. Initialise once from index.js; fire events from App.js.
//
// The event set is deliberately small and maps to the launch funnel in
// docs/gtm-playbook.md §11 (signup → workspace → room → message → decision,
// plus explicit AI invocation). Each event corresponds to a deliberate user
// action so the activation/retention funnels stay clean and double-count-free.

import posthog from 'posthog-js';

let enabled = false;

// Initialise PostHog. No-op (and never throws) when the key is unset or we're
// not in a browser, so importing this module is always safe.
export function initAnalytics() {
  const key = process.env.REACT_APP_POSTHOG_KEY;
  if (enabled || !key || typeof window === 'undefined') return;
  try {
    posthog.init(key, {
      api_host: process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com',
      capture_pageview: true,
      // We fire named product events explicitly; autocapture would add noise
      // and ship DOM text we don't want leaving the browser.
      autocapture: false,
      persistence: 'localStorage+cookie',
    });
    enabled = true;
  } catch {
    // Never let analytics init break app boot.
    enabled = false;
  }
}

// Tie subsequent events to a known user. Call on auth success.
export function identifyUser(user) {
  if (!enabled || !user || user.id === undefined || user.id === null) return;
  try {
    posthog.identify(String(user.id), {
      email: user.email,
      name: user.name,
    });
  } catch {
    // ignore — analytics must never surface to the user
  }
}

// Fire a named product event. Silently dropped when analytics is inert.
export function track(event, props) {
  if (!enabled) return;
  try {
    posthog.capture(event, props || {});
  } catch {
    // ignore
  }
}

// Clear the identified user (call on logout) so the next user starts clean.
export function resetAnalytics() {
  if (!enabled) return;
  try {
    posthog.reset();
  } catch {
    // ignore
  }
}

// Test/introspection helper — whether PostHog actually initialised.
export function analyticsEnabled() {
  return enabled;
}
